// useLiveElapsed — a smoothly ticking elapsed read for a streaming card.
//
// The material build stamps its elapsed (`stampedMs` = ms since the build
// began) only when a server delta arrives, so read raw it FREEZES between
// deltas — sitting at "0:01" until the next event jumps it to "0:53" (F114).
// We recover the build's start INSTANT — `startedAt = now - stampedMs`, pinned
// the moment a fresh stamp lands — then project elapsed off the ticking
// `useNow` seam: `elapsed = now - startedAt`. It re-pins on every new stamp, so
// it self-corrects to server truth and never drifts. `running` false (the build
// settled or stopped) freezes the read at the final stamp — no post-completion
// ticking. This is the ui-projection discipline's sanctioned clock path: the
// wall clock enters ONLY through `useNow`, never a component-local timer.

import { useRef } from "react";
import { useNow } from "./useNow.js";

export const useLiveElapsed = (stampedMs: number, running: boolean): number => {
    // Tick once a second — the smallest cadence a m:ss read needs.
    const now = useNow(1_000);
    // The inferred build-start instant, pinned when the server stamp changes.
    // A ref (not state): a view-ephemeral clock anchor, no domain meaning, does
    // not survive reload — re-derived from the next stamp on a fresh mount.
    const anchor = useRef<{
        readonly stampedMs: number;
        readonly startedAt: number;
    }>({ stampedMs, startedAt: now - stampedMs });
    if (anchor.current.stampedMs !== stampedMs) {
        anchor.current = { stampedMs, startedAt: now - stampedMs };
    }
    // Frozen once done: the final stamp is the honest total, not a live tick.
    if (!running) return stampedMs;
    // Never read below the last server stamp (guards a backwards clock step).
    return Math.max(stampedMs, now - anchor.current.startedAt);
};
